import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import { canView, isPublicPath, landingPath } from '../lib/access'
import type { Me } from '../lib/auth'
import { FORBIDDEN_EVENT, UNAUTHORIZED_EVENT, type ForbiddenEventDetail } from '../lib/http'
import { useAccess, useSession } from '../lib/session'
import {
  ForcedPasswordScreen,
  LoginScreen,
  NoAccess,
  ReauthDialog,
  SessionErrorScreen,
  Toast,
  type ToastMessage,
} from './auth/AccessScreens'
import { SessionProvider } from './auth/SessionProvider'
import { UserMenu } from './auth/UserMenu'
import { LangSwitch } from './LangSwitch'
import { LANG, langChosenHere, setLang } from '../i18n/lang'
import { useHomeView } from '../lib/useHomeView'
import { LegiaHubWordmark } from './LegiaHubWordmark'
import { TeamCrest } from './TeamCrest'

// Nasze logo (StatsBomb id Legii = OUR_TEAM_ID w backendzie). TeamCrest ładuje
// z R2 (748.png) → lokalnie → dysk z inicjałem.
const OUR_TEAM_ID = 748

interface TabDef {
  to: string
  label: string
  // Skrót na węższych ekranach (< 1536 px): przy 1280 px pełne nazwy wszystkich
  // zakładek + konto nie mieszczą się w jednej belce. Pełna nazwa zostaje w podpowiedzi.
  short?: string
  disabled?: boolean
  // active gdy bieżąca ścieżka == lub zaczyna się od któregokolwiek z prefiksów.
  // Zakładki-parasole (Analiza przeciwnika, Trening) skupiają kilka podzakładek.
  prefixes: string[]
  // Podzakładki w kolejności paska — przy włączonej fladze zakładka jest widoczna,
  // gdy konto widzi którąkolwiek z nich, i prowadzi do pierwszej widocznej
  // (sekcje stron: lib/access.ts).
  entries: string[]
  // Strona za logowaniem NIEZALEŻNIE od flagi AUTH_REQUIRED (Squad planner) — zakładka
  // tylko z prawdziwym dostępem konta, także lokalnie przy wyłączonej fladze.
  alwaysGated?: boolean
  // Inna nazwa i kolejność podzakładek w module skautingowym (Scout / Dyrektor sportowy —
  // lib/useHomeView.ts); prefiksy zostają wspólne.
  scout?: Pick<TabDef, 'label' | 'short' | 'entries'>
}

const TABS: TabDef[] = [
  // Kolejność i nazwy (każde słowo wielką literą) — korekta sztabu 26.10.2026.
  // Strona startowa — każde konto (bez sekcji w lib/access.ts).
  { to: '/home', label: 'Start', prefixes: ['/home'], entries: ['/home'] },
  // Legia II + akademia z Teamworks.
  { to: '/kalendarz', label: 'Kalendarz', prefixes: ['/kalendarz'], entries: ['/kalendarz'] },
  {
    // Skróty poniżej 2xl. Bez Excela w belce (26.10.2026) przy 1280 px jako admin zapas
    // to +152 px ze skrótami i +38,8 px z pełnymi nazwami (wcześniej −92 px bez skrótów).
    // Moduł skautingowy: „Skauting" od profilu ligi (28.10.2026) — sztab dalej „Analiza Rywala".
    to: '/dossier', label: 'Analiza Rywala', short: 'Analiza', prefixes: ['/dossier', '/liga', '/gracze', '/sezon', '/szanse', '/mapa'],
    entries: ['/dossier', '/gracze', '/mapa', '/sezon', '/szanse'],
    scout: { label: 'Skauting', entries: ['/liga', '/gracze', '/dossier', '/mapa', '/sezon', '/szanse'] },
  },
  {
    to: '/raporty', label: 'Raporty Meczowe', short: 'Raporty', prefixes: ['/raporty'],
    entries: ['/raporty', '/raporty/podsumowanie', '/raporty/live', '/raporty/sfg', '/raporty/legia-ii'],
  },
  {
    to: '/raport-trening', label: 'Trening', prefixes: ['/raport-trening', '/trening', '/bramkarze'],
    entries: ['/raport-trening', '/trening', '/bramkarze'],
  },
  { to: '/motoryka', label: 'Motoryka', prefixes: ['/motoryka'], entries: ['/motoryka'] },
  // Badania VALD — za logowaniem niezależnie od flagi (dane o charakterze zdrowotnym), jak
  // Squad planner: zakładka tylko z prawdziwym dostępem do sekcji `vald` (admin, motoryka).
  { to: '/vald', label: 'VALD', prefixes: ['/vald'], entries: ['/vald'], alwaysGated: true },
  {
    to: '/karta-zawodnika', label: 'Zawodnicy', prefixes: ['/karta-zawodnika', '/akademia', '/rankingi', '/procedury'],
    entries: ['/karta-zawodnika', '/akademia', '/rankingi', '/procedury'],
  },
  { to: '/squad-planner', label: 'Squad Planner', prefixes: ['/squad-planner'], entries: ['/squad-planner'], alwaysGated: true },
]

export function AppShell() {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  )
}

// Bramka logowania + nawigacja wg dostępu działają TYLKO przy włączonej fladze
// AUTH_REQUIRED (GET /api/auth/config). Przy wyłączonej powłoka wygląda i działa
// jak dawniej; jedyny dodatek to menu konta, gdy ktoś jest zalogowany.
function Shell() {
  const { pathname, search } = useLocation()
  const { me, loading, error, setMe, reload, config, authRequired } = useSession()
  const access = useAccess()
  const { view, switchable } = useHomeView()
  // Podgląd modułu admina (?widok=) jedzie z zakładkami, jak z pigułkami SubTabLayout —
  // inaczej klik w „Skauting" zrzucałby admina z powrotem do widoku sztabu.
  const widok = switchable ? new URLSearchParams(search).get('widok') : null

  // ── 401 / 403 z API (lib/http.ts) — słuchamy tylko przy włączonej fladze ──
  const [reauth, setReauth] = useState(false)
  const [toast, setToast] = useState<ToastMessage | null>(null)
  const closeToast = useCallback(() => setToast(null), [])
  const closeReauth = useCallback(() => setReauth(false), [])
  // Okno ponownego logowania ma sens tylko, gdy sesja BYŁA (wygasła w trakcie
  // pracy). 401 spóźnionego zapytania po świadomym wylogowaniu go nie otwiera.
  const hadSession = useRef(false)
  useEffect(() => { hadSession.current = !!me }, [me])

  useEffect(() => {
    if (!authRequired) return
    const onUnauthorized = () => { if (hadSession.current) setReauth(true) }
    const onForbidden = (e: Event) => {
      const { detail } = (e as CustomEvent<ForbiddenEventDetail>).detail
      setToast({ id: Date.now(), tone: 'warn', text: detail })
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    window.addEventListener(FORBIDDEN_EVENT, onForbidden)
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
      window.removeEventListener(FORBIDDEN_EVENT, onForbidden)
    }
  }, [authRequired])

  // Przeglądarka bez własnego wyboru języka przejmuje język konta (migr. 072).
  useEffect(() => {
    if (me?.lang && !langChosenHere && me.lang !== LANG) setLang(me.lang, { persistAccount: false })
  }, [me])

  const onLoggedIn = useCallback((m: Me) => { setReauth(false); setMe(m) }, [setMe])
  const onReauthed = useCallback((m: Me) => {
    setReauth(false)
    setMe(m)
    setToast({ id: Date.now(), tone: 'ok', text: 'Zalogowano — powtórz ostatnią akcję.' })
  }, [setMe])
  const onLoggedOut = useCallback(() => { setReauth(false); setMe(null) }, [setMe])

  // ── Bramka ──
  // Do czasu odpowiedzi /config nie wiadomo, czy wpuszczać — treść i zakładki
  // czekają (jedno małe zapytanie, równolegle z /me); przy fladze czekamy też na /me.
  const pending = config === null || (authRequired && loading)
  const gated = authRequired && !isPublicPath(pathname)
  if (!pending && gated) {
    if (!me && error) return <SessionErrorScreen error={error} onRetry={reload} />
    if (!me) return <LoginScreen onLoggedIn={onLoggedIn} />
    if (me.user.must_change_password) {
      return <ForcedPasswordScreen me={me} onChanged={reload} onLoggedOut={onLoggedOut} />
    }
  }
  const allowed = !gated || canView(me, pathname)

  const tabs = TABS.flatMap((base) => {
    const tab = base.scout && view !== 'sztab' ? { ...base, ...base.scout, short: base.scout.short } : base
    if (tab.alwaysGated) return canView(me, tab.to) ? [{ tab, to: tab.to }] : []
    if (!access.enforced) return [{ tab, to: tab.entries[0] }]
    const first = tab.entries.find((p) => access.canView(p))
    return first ? [{ tab, to: first }] : []
  })
  // Przy włączonej fladze bez sesji (np. publiczne /konto) — sama belka, bez zakładek.
  const showNav = !pending && (!authRequired || !!me)

  return (
    <div className="flex min-h-full flex-col">
      {/* Belka Legia Hub (projekt z Claude Design, 15.10.2026): znak, zakładki-pigułki,
          konto w jednym ciemnym pasku na całą szerokość. */}
      <header className="no-print sticky top-0 z-30 bg-forest-800 text-white">
        <div className="flex h-[60px] items-center gap-6 px-7">
          <Link to="/home" className="flex flex-none items-center gap-2.5" title="Strona startowa">
            <TeamCrest teamId={OUR_TEAM_ID} teamName="Legia Warszawa" size={30} />
            <LegiaHubWordmark className="text-sm" />
          </Link>

          <nav className="min-w-0 flex-1">
            <ul className="flex gap-1 overflow-x-auto text-[13px] font-semibold [scrollbar-width:none]">
              {/* Pusty pasek trzyma wysokość, póki nie wiadomo, które zakładki pokazać. */}
              {!showNav && <li aria-hidden className="invisible px-3 py-2">&nbsp;</li>}
              {showNav && tabs.map(({ tab, to }) => {
                const isActive = tab.prefixes.some(
                  (p) => pathname === p || pathname.startsWith(p + '/'),
                )

                if (tab.disabled) {
                  return (
                    <li key={tab.to}>
                      <span className="inline-flex cursor-not-allowed items-center gap-2 whitespace-nowrap px-3 py-2 text-white/35" title="wkrótce">
                        {tab.label}
                      </span>
                    </li>
                  )
                }
                return (
                  <li key={tab.to}>
                    <NavLink
                      to={widok ? `${to}?widok=${encodeURIComponent(widok)}` : to}
                      title={tab.short ? tab.label : undefined}
                      className={[
                        'inline-flex items-center whitespace-nowrap rounded-lg px-3 py-2 transition-colors',
                        isActive ? 'bg-white/[0.12] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white',
                      ].join(' ')}
                    >
                      {tab.short ? (
                        <>
                          <span className="hidden 2xl:inline">{tab.label}</span>
                          <span className="2xl:hidden">{tab.short}</span>
                        </>
                      ) : tab.label}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className="flex flex-none items-center gap-3">
            <LangSwitch />
            {me && <UserMenu me={me} onLoggedOut={onLoggedOut} />}
          </div>
        </div>
      </header>

      <main className="flex-1">
        {pending ? null : allowed ? <Outlet /> : <NoAccess landing={landingPath(me)} />}
      </main>

      {authRequired && reauth && me && <ReauthDialog onLoggedIn={onReauthed} onClose={closeReauth} />}
      {authRequired && <Toast toast={toast} onClose={closeToast} />}
    </div>
  )
}
